import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  buildBroadcastUrl,
  CALLOUT_KINDS,
  describeLayoutError,
  createScene,
  deleteScene,
  duplicateScene,
  patchScene,
  postControlCallout,
  postTake,
  putControlOnAir,
  putControlLayout,
  putDefaultTransition,
  putPreviewScene,
  putSceneOrder,
  type BroadcastLayout,
  type BroadcastScene,
  type SceneTransition,
  type BroadcastSessionState,
  type BroadcastState,
} from "../../lib/broadcastApi";
import { copyTextToClipboard } from "../../lib/clipboard";
import {
  SceneTransitionHost,
  clampTransitionMs,
} from "./animation/sceneTransition";
import AssetLibrary from "./AssetLibrary";
import ScenesPanel from "./ScenesPanel";
import { useAssetLibrary } from "./useAssetLibrary";
import Stage, { useElementSize } from "./Stage";
import StageWidgets from "./StageWidgets";
import UnavailableFrame from "./UnavailableFrame";
import { useBroadcastDocument } from "./useBroadcastDocument";
import { useBroadcastFeed } from "./useBroadcastFeed";
import { useControlActivity } from "./useControlActivity";
import { computeStageScale, STAGE_SIZES } from "./stageScale";
import EditorOverlay from "./EditorOverlay";
import PresetsPanel from "./PresetsPanel";
import WidgetDrawer from "./WidgetDrawer";
import WidgetsPanel from "./WidgetsPanel";
import { createWidget, type Rect } from "./editorMath";

const SAVE_DEBOUNCE_MS = 300;
/** REST fallback cadence: fast without the control socket, slow with it. */
const POLL_MS = 5000;
const POLL_WITH_SOCKET_MS = 30_000;
const DEFAULT_TRANSITION: SceneTransition = { type: "fade", duration_ms: 500 };

interface SceneState {
  scenes: BroadcastScene[];
  programId: string;
  previewId: string;
  transition: SceneTransition;
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** `/broadcast/control/:token`: producer control page (B1 minimal). */
export default function ControlPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { t } = useTranslation("broadcast");
  useBroadcastDocument(false, false);

  const [broadcastToken, setBroadcastToken] = useState<string | null>(null);
  const [online, setOnline] = useState(true);
  const activity = useControlActivity();
  const [air, setAir] = useState<{
    state: BroadcastSessionState | null;
    autoOffAt: string | null;
    expiresAt: string | null;
  }>({ state: null, autoOffAt: null, expiresAt: null });
  const [sceneState, setSceneState] = useState<SceneState | null>(null);
  const [socketUp, setSocketUp] = useState(false);
  const library = useAssetLibrary(token, activity.paused);
  const baseFeed = useBroadcastFeed(token, {
    control: true,
    socket: true,
    pollMs: socketUp ? POLL_WITH_SOCKET_MS : POLL_MS,
    paused: activity.paused,
    onState: (s: BroadcastState) => {
      setOnline(true);
      if (s.scenes && s.program_scene_id) {
        setSceneState({
          scenes: s.scenes,
          programId: s.program_scene_id,
          previewId: s.preview_scene_id ?? s.program_scene_id,
          transition: s.default_transition ?? DEFAULT_TRANSITION,
        });
      }
      setAir({
        state: s.state ?? (s.on_air === false ? "off" : "on_air"),
        autoOffAt: s.auto_off_at ?? null,
        expiresAt: s.expires_at ?? null,
      });
      if (s.broadcast_url_token) setBroadcastToken(s.broadcast_url_token);
    },
    onFetchError: () => setOnline(false),
  });

  // Library urls join the state's asset map so a freshly picked image shows
  // in the preview before the next state refresh.
  const feed = useMemo(
    () => ({
      ...baseFeed,
      assets: {
        ...baseFeed.assets,
        ...Object.fromEntries(library.assets.map((x) => [x.asset_id, x.url])),
      },
    }),
    [baseFeed, library.assets],
  );

  // Local layout shown while a debounced PUT is pending (last write wins).
  const [local, setLocal] = useState<BroadcastLayout | null>(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const pendingRef = useRef<BroadcastLayout | null>(null);
  const pendingSceneRef = useRef<string | null>(null);
  const sceneStateRef = useRef<SceneState | null>(null);
  sceneStateRef.current = sceneState;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyLayout = feed.applyLayout;

  const flush = useCallback(async () => {
    const next = pendingRef.current;
    if (!next) return;
    const sceneId = pendingSceneRef.current;
    pendingRef.current = null;
    setSaving(true);
    try {
      if (sceneId) {
        // Editing targets the PREVIEW scene; if it is also the program scene
        // the server publishes it as a live (smooth) edit.
        const res = await patchScene(token, sceneId, { layout: next });
        setSceneState((prev) =>
          prev
            ? {
                ...prev,
                scenes: prev.scenes.map((sc) =>
                  sc.scene_id === sceneId ? res.scene : sc,
                ),
              }
            : prev,
        );
      } else {
        const saved = await putControlLayout(token, next);
        applyLayout(saved);
      }
      setSaveError("");
      if (!pendingRef.current) setLocal(null);
    } catch (err) {
      setSaveError(describeLayoutError(err) || t("control.saveFailed"));
      if (!pendingRef.current) setLocal(null);
    } finally {
      setSaving(false);
    }
  }, [token, applyLayout, t]);

  const update = useCallback(
    (mutate: (draft: BroadcastLayout) => void) => {
      const cur = sceneStateRef.current;
      const previewScene = cur?.scenes.find(
        (x) => x.scene_id === cur.previewId,
      );
      const base =
        pendingRef.current ?? local ?? previewScene?.layout ?? feed.layout;
      if (!base) return;
      const next = clone(base);
      mutate(next);
      pendingRef.current = next;
      pendingSceneRef.current = previewScene?.scene_id ?? null;
      setLocal(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [local, feed.layout, flush],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const [previewRef, previewSize] = useElementSize<HTMLDivElement>();
  const [programRef, programSize] = useElementSize<HTMLDivElement>();
  const [sceneBusy, setSceneBusy] = useState(false);
  const socketConnected = feed.socketState === "connected";
  useEffect(() => setSocketUp(socketConnected), [socketConnected]);
  const [copied, setCopied] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snap, setSnap] = useState(true);

  const onRect = useCallback(
    (id: string, rect: Rect) =>
      update((d) => {
        const w = d.widgets.find((x) => x.id === id);
        if (w) Object.assign(w, rect);
      }),
    [update],
  );
  const loadPreset = useCallback(
    (preset: BroadcastLayout) =>
      update((d) => {
        d.stage = preset.stage;
        d.widgets = preset.widgets;
        d.callouts = preset.callouts;
      }),
    [update],
  );

  if (feed.status === "unavailable") return <UnavailableFrame />;
  const previewScene = sceneState?.scenes.find(
    (x) => x.scene_id === sceneState.previewId,
  );
  const programScene = sceneState?.scenes.find(
    (x) => x.scene_id === sceneState.programId,
  );
  const layout = local ?? previewScene?.layout ?? feed.layout;
  if (!layout) {
    return (
      <div
        className="min-h-screen bg-slate-900 text-slate-100 p-8"
        data-testid="control-loading"
      >
        {t("control.loading")}
      </div>
    );
  }

  const stage = layout.stage;
  const dims = STAGE_SIZES[stage.aspect];
  const broadcastUrl = broadcastToken
    ? buildBroadcastUrl(window.location.origin, broadcastToken)
    : "";

  const copyLink = async () => {
    if (!broadcastUrl) return;
    if (await copyTextToClipboard(broadcastUrl)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const airOn = air.state === "on_air";
  const airLocked = air.state === "expired" || air.state === "revoked";
  const toggleOnAir = async () => {
    try {
      const res = await putControlOnAir(token, !airOn);
      setAir((a) => ({ ...a, state: res.state }));
      setSaveError("");
    } catch {
      setSaveError(t("control.onAir.failed"));
    }
  };
  const fmtTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : "";

  const sceneAction = async (fn: () => Promise<unknown>) => {
    // Finish any pending layout save first so the action sees it.
    if (timerRef.current) clearTimeout(timerRef.current);
    await flush();
    setSceneBusy(true);
    try {
      await fn();
      setSaveError("");
    } catch (err) {
      setSaveError(describeLayoutError(err) || t("control.scenes.failed"));
    } finally {
      setSceneBusy(false);
      await feed.refresh();
    }
  };

  const addImageWidget = (assetId: string) => {
    const created = createWidget("image", layout.widgets, layout.stage.aspect);
    if (!created) return;
    created.options = { asset_id: assetId, fit: "contain" };
    update((d) => {
      d.widgets.push(created);
    });
    setSelectedId(created.id);
  };
  const useBackgroundImage = (assetId: string) =>
    update((d) => {
      d.stage.background = "image";
      d.stage.background_asset_id = assetId;
    });

  const sendCallout = (action: "dismiss" | "replay") => {
    void postControlCallout(token, action).catch(() =>
      setSaveError(t("control.saveFailed")),
    );
  };

  const fieldCls =
    "rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100";
  const labelCls = "block text-sm font-medium text-slate-200 mb-1";

  return (
    <div
      className="min-h-screen bg-slate-900 text-slate-100"
      data-testid="broadcast-control-page"
    >
      <header className="border-b border-slate-700 px-6 py-4 flex items-center justify-between gap-4">
        <h1 className="text-xl font-bold">{t("control.title")}</h1>
        <div className="flex items-center gap-4 text-sm">
          <span
            data-testid="control-connection"
            data-online={online ? "true" : "false"}
            className={online ? "text-emerald-300" : "text-amber-300"}
          >
            {online ? t("control.connected") : t("control.reconnecting")}
          </span>
          <span data-testid="control-save-state" className="text-slate-300">
            {saving ? t("control.saving") : t("control.saved")}
          </span>
        </div>
      </header>

      {activity.idle && (
        <div
          role="alert"
          data-testid="control-idle-banner"
          className="flex items-center justify-between gap-4 border-b border-amber-500 bg-amber-900/40 px-6 py-3 text-sm"
        >
          <span>{t("control.idle.message")}</span>
          <button
            type="button"
            data-testid="control-reconnect"
            onClick={activity.reconnect}
            className="rounded-md bg-primary-600 px-3 py-1 text-sm font-semibold text-white hover:bg-primary-700"
          >
            {t("control.idle.reconnect")}
          </button>
        </div>
      )}

      {air.state && (
        <div
          data-testid="control-on-air"
          data-state={air.state}
          className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-700 px-6 py-3 text-sm"
        >
          <button
            type="button"
            role="switch"
            aria-checked={airOn}
            aria-label={t("control.onAir.label")}
            data-testid="control-on-air-toggle"
            disabled={airLocked}
            onClick={toggleOnAir}
            className={`rounded-full px-4 py-1 font-semibold disabled:opacity-50 ${
              airOn ? "bg-red-600 text-white" : "bg-slate-700 text-slate-100"
            }`}
          >
            {airOn ? t("control.onAir.on") : t("control.onAir.off")}
          </button>
          <span data-testid="control-on-air-state">
            {t(`control.onAir.states.${air.state}`)}
          </span>
          {air.autoOffAt && (
            <span data-testid="control-auto-off-at">
              {t("control.onAir.autoOffAt", { time: fmtTime(air.autoOffAt) })}
            </span>
          )}
          {air.expiresAt && (
            <span data-testid="control-expires-at">
              {t("control.onAir.expiresAt", { time: fmtTime(air.expiresAt) })}
            </span>
          )}
          <span
            data-testid="control-on-air-note"
            className="text-slate-300 text-xs"
          >
            {t("control.onAir.note")}
          </span>
        </div>
      )}

      <main className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <section aria-label={t("control.preview")}>
          <h2 className="text-lg font-semibold mb-2">{t("control.preview")}</h2>
          <div
            ref={previewRef}
            data-testid="control-preview"
            className="relative w-full rounded-lg overflow-hidden border border-slate-700"
            style={{ aspectRatio: `${dims.w} / ${dims.h}` }}
          >
            <Stage
              stage={stage}
              branding={feed.broadcaster}
              assets={feed.assets}
              fit={previewSize}
              checker
            >
              <StageWidgets layout={layout} feed={feed} />
              <EditorOverlay
                layout={layout}
                scale={computeStageScale(
                  stage.aspect,
                  previewSize.width,
                  previewSize.height,
                )}
                selectedId={selectedId}
                snap={snap}
                onSelect={setSelectedId}
                onRect={onRect}
              />
            </Stage>
          </div>

          {sceneState && sceneState.previewId === sceneState.programId && (
            <p
              data-testid="control-editing-program"
              className="mt-1 text-xs text-red-300"
            >
              {t("control.scenes.editingProgram")}
            </p>
          )}

          {programScene && sceneState && (
            <div className="mt-4" data-testid="control-program-section">
              <h3 className="text-sm font-semibold mb-1">
                {t("control.scenes.programMonitor")}
              </h3>
              <div
                ref={programRef}
                data-testid="control-program-monitor"
                data-scene-id={sceneState.programId}
                className="relative w-full max-w-md rounded-lg overflow-hidden border border-red-500"
                style={{
                  aspectRatio: `${STAGE_SIZES[programScene.layout.stage.aspect].w} / ${
                    STAGE_SIZES[programScene.layout.stage.aspect].h
                  }`,
                }}
              >
                <SceneTransitionHost
                  sceneKey={sceneState.programId}
                  type={sceneState.transition.type}
                  durationMs={clampTransitionMs(
                    sceneState.transition.duration_ms,
                  )}
                >
                  <Stage
                    stage={programScene.layout.stage}
                    branding={feed.broadcaster}
                    assets={feed.assets}
                    fit={programSize}
                    checker
                  >
                    <StageWidgets
                      layout={programScene.layout}
                      feed={feed}
                      onAir
                    />
                  </Stage>
                </SceneTransitionHost>
              </div>
            </div>
          )}

          <label className="mt-2 flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              data-testid="control-snap-toggle"
              checked={snap}
              onChange={(e) => setSnap(e.target.checked)}
            />
            {t("control.snap")}
          </label>

          <div className="mt-4 rounded-lg border border-slate-700 p-4">
            <h3 className="font-semibold mb-2">{t("control.broadcastLink")}</h3>
            <div className="flex gap-2">
              <input
                readOnly
                aria-label={t("control.broadcastLink")}
                data-testid="control-broadcast-link"
                value={broadcastUrl}
                className={`${fieldCls} flex-1`}
              />
              <button
                type="button"
                onClick={copyLink}
                data-testid="control-copy-link"
                className="rounded-md bg-primary-600 px-3 py-1 text-sm font-semibold text-white hover:bg-primary-700"
              >
                {copied ? t("copied") : t("copy")}
              </button>
            </div>
          </div>
        </section>

        <section className="space-y-6">
          {sceneState && (
            <ScenesPanel
              scenes={sceneState.scenes}
              programId={sceneState.programId}
              previewId={sceneState.previewId}
              transition={sceneState.transition}
              busy={sceneBusy}
              onPreview={(id) =>
                void sceneAction(() => putPreviewScene(token, id))
              }
              onTake={(id, tr) =>
                void sceneAction(() => postTake(token, id, tr))
              }
              onAspect={(id, aspect) =>
                void sceneAction(() => patchScene(token, id, { aspect }))
              }
              onCreate={() =>
                void sceneAction(() =>
                  createScene(token, {
                    name: t("control.scenes.newName", {
                      n: sceneState.scenes.length + 1,
                    }),
                    aspect: previewScene?.aspect,
                    from_scene_id: previewScene?.scene_id,
                  }),
                )
              }
              onDuplicate={(id) =>
                void sceneAction(() => duplicateScene(token, id))
              }
              onDelete={(id) => void sceneAction(() => deleteScene(token, id))}
              onRename={(id, name) =>
                void sceneAction(() => patchScene(token, id, { name }))
              }
              onMove={(id, delta) => {
                const ids = sceneState.scenes.map((x) => x.scene_id);
                const i = ids.indexOf(id);
                const j = i + delta;
                if (i < 0 || j < 0 || j >= ids.length) return;
                [ids[i], ids[j]] = [ids[j], ids[i]];
                void sceneAction(() => putSceneOrder(token, ids));
              }}
              onTransition={(tr) => {
                setSceneState((prev) =>
                  prev ? { ...prev, transition: tr } : prev,
                );
                void sceneAction(() => putDefaultTransition(token, tr));
              }}
            />
          )}

          <div className="rounded-lg border border-slate-700 p-4 space-y-4">
            <h2 className="text-lg font-semibold">{t("control.stage")}</h2>

            <div>
              <label className={labelCls} htmlFor="ctl-aspect">
                {t("control.aspect")}
              </label>
              <select
                id="ctl-aspect"
                data-testid="control-aspect"
                className={fieldCls}
                value={stage.aspect}
                onChange={(e) =>
                  update((d) => {
                    d.stage.aspect = e.target.value as "16:9" | "16:10";
                  })
                }
              >
                <option value="16:9">16:9 (1920x1080)</option>
                <option value="16:10">16:10 (1920x1200)</option>
              </select>
            </div>

            <div>
              <label className={labelCls} htmlFor="ctl-background">
                {t("control.background")}
              </label>
              <select
                id="ctl-background"
                data-testid="control-background"
                className={fieldCls}
                value={stage.background}
                onChange={(e) =>
                  update((d) => {
                    d.stage.background = e.target
                      .value as BroadcastLayout["stage"]["background"];
                  })
                }
              >
                {(
                  [
                    "transparent",
                    "solid",
                    "branded",
                    "chroma",
                    "image",
                  ] as const
                ).map((b) => (
                  <option key={b} value={b}>
                    {t(`control.backgrounds.${b}`)}
                  </option>
                ))}
              </select>
              {stage.background === "image" && (
                <select
                  aria-label={t("control.backgroundImage")}
                  data-testid="control-background-asset"
                  className={`${fieldCls} ml-3`}
                  value={stage.background_asset_id ?? ""}
                  onChange={(e) =>
                    update((d) => {
                      d.stage.background_asset_id = e.target.value || null;
                    })
                  }
                >
                  <option value="">{t("control.panel.noImage")}</option>
                  {library.assets
                    .filter((a) => a.kind === "background")
                    .map((a) => (
                      <option key={a.asset_id} value={a.asset_id}>
                        {a.width}x{a.height}
                      </option>
                    ))}
                </select>
              )}
              {stage.background === "solid" && (
                <input
                  type="color"
                  aria-label={t("control.backgroundColor")}
                  data-testid="control-background-color"
                  className="ml-3 h-8 w-12 align-middle"
                  value={stage.background_color}
                  onChange={(e) =>
                    update((d) => {
                      d.stage.background_color = e.target.value;
                    })
                  }
                />
              )}
            </div>

            <div>
              <label className={labelCls} htmlFor="ctl-text-scale">
                {t("control.textScale")}:{" "}
                <span data-testid="control-text-scale-value">
                  {stage.global_text_scale}
                </span>
              </label>
              <input
                id="ctl-text-scale"
                data-testid="control-text-scale"
                type="range"
                min={0.5}
                max={2}
                step={0.05}
                value={stage.global_text_scale}
                onChange={(e) =>
                  update((d) => {
                    d.stage.global_text_scale = Number(e.target.value);
                  })
                }
                className="w-full"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="ctl-delay">
                {t("control.dataDelay")}:{" "}
                <span data-testid="control-delay-value">
                  {(stage.data_delay_ms / 1000).toFixed(1)} s
                </span>
              </label>
              <input
                id="ctl-delay"
                data-testid="control-delay"
                type="range"
                min={0}
                max={60000}
                step={500}
                value={stage.data_delay_ms}
                onChange={(e) =>
                  update((d) => {
                    d.stage.data_delay_ms = Number(e.target.value);
                  })
                }
                className="w-full"
              />
              <p className="mt-1 text-xs text-slate-300">
                {t("control.dataDelayHint")}
              </p>
            </div>
          </div>

          <AssetLibrary
            library={library}
            onAddImageWidget={addImageWidget}
            onUseBackground={useBackgroundImage}
          />

          <WidgetsPanel
            layout={layout}
            selectedId={selectedId}
            onSelect={setSelectedId}
            update={update}
          />
          <WidgetDrawer
            layout={layout}
            library={library.assets}
            widget={layout.widgets.find((w) => w.id === selectedId) ?? null}
            update={update}
          />
          <PresetsPanel token={token} layout={layout} onLoad={loadPreset} />

          <div className="rounded-lg border border-slate-700 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("control.callouts")}</h2>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="control-callout-dismiss"
                  onClick={() => sendCallout("dismiss")}
                  className="rounded-md border border-slate-500 px-3 py-1 text-sm hover:bg-slate-800"
                >
                  {t("control.dismiss")}
                </button>
                <button
                  type="button"
                  data-testid="control-callout-replay"
                  onClick={() => sendCallout("replay")}
                  className="rounded-md border border-slate-500 px-3 py-1 text-sm hover:bg-slate-800"
                >
                  {t("control.replay")}
                </button>
              </div>
            </div>
            {CALLOUT_KINDS.map((kind) => {
              const c = layout.callouts[kind];
              return (
                <div
                  key={kind}
                  data-testid={`control-callout-row-${kind}`}
                  className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1"
                >
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      data-testid={`control-callout-enabled-${kind}`}
                      checked={c.enabled}
                      onChange={(e) =>
                        update((d) => {
                          d.callouts[kind].enabled = e.target.checked;
                        })
                      }
                    />
                    {t(`callout.${kind}`)}
                  </label>
                  <span className="text-xs text-slate-300">
                    {(c.duration_ms / 1000).toFixed(1)} s
                  </span>
                  <input
                    type="range"
                    aria-label={`${t(`callout.${kind}`)} ${t("control.duration")}`}
                    data-testid={`control-callout-duration-${kind}`}
                    min={2000}
                    max={15000}
                    step={500}
                    value={c.duration_ms}
                    onChange={(e) =>
                      update((d) => {
                        d.callouts[kind].duration_ms = Number(e.target.value);
                      })
                    }
                    className="col-span-2 w-full"
                  />
                </div>
              );
            })}
          </div>

          {saveError && (
            <p
              role="alert"
              data-testid="control-save-error"
              className="rounded-md border border-red-500 bg-red-950 p-3 text-sm text-red-200"
            >
              {saveError}
            </p>
          )}

          <details
            open
            data-testid="control-setup-guide"
            className="rounded-lg border border-slate-700 p-4"
          >
            <summary className="cursor-pointer text-lg font-semibold">
              {t("guide.title")}
            </summary>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-slate-200">
              <li>{t("guide.obsSource")}</li>
              <li>{t("guide.obsSize")}</li>
              <li>{t("guide.transparency")}</li>
              <li>{t("guide.delay")}</li>
              <li>{t("guide.callouts")}</li>
            </ol>
            <h3 className="mt-4 font-semibold">{t("guide.limitsTitle")}</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-slate-200">
              <li>{t("guide.limitBall")}</li>
              <li>{t("guide.limitDelay")}</li>
            </ul>
          </details>
        </section>
      </main>
    </div>
  );
}
